import * as React from 'react';
import { deriveEventPlainDescription, getEventRepetitionValidationError } from 'cb-model';
import { EventErrors, EventFormValues } from './eventDetailsFormTypes';

export const hasPaidTickets = (tickets: EventFormValues['tickets'] | undefined): boolean =>
  (tickets || []).some((ticket) => Number(ticket?.price ?? 0) > 0);

export const getOffsiteBookingUrlError = (value?: string): string | undefined => {
  if (!value) return 'Booking URL is required for offsite booking.';
  if (!value.startsWith('http')) return "Booking URL must start with 'http' or 'https'.";
  return undefined;
};

export const buildEventFormErrors = (value: EventFormValues, params: { isPaidEvent: boolean }): string[] => {
  const { isPaidEvent } = params;
  const errors: string[] = [];
  const required = (field: keyof EventFormValues, message: string) => {
    if (!value[field]) errors.push(message);
  };

  required('title', 'Title is required.');
  if (!value.markdown_description) {
    errors.push('Description is required.');
  }
  required('type', 'Type is required.');
  if (!value.event_profile_id) errors.push('Event Profile is required.');
  if (['noAttendance', 'full'].includes(value.flow_option) && !value.feedback_form_id) {
    errors.push('Feedback Form is required.');
  }
  required('location_type', 'Location Type is required.');

  if (['in-person', 'hybrid'].includes(value.location_type) && !value.location) {
    errors.push('Location is required for in-person/hybrid events.');
  }

  const isOnDemand = value.type === 'on-demand';
  if (!isOnDemand) {
    if (!value.start_date || !value.end_date) {
      errors.push('Start and end date/time are required.');
    } else if (value.end_date < value.start_date) {
      errors.push('End date/time must be after start date/time.');
    }
  } else if (value.start_date || value.end_date) {
    errors.push('On-demand events should not have start/end dates.');
  }
  const repetitionError = getEventRepetitionValidationError(value.repetition, value.start_date);
  if (repetitionError) errors.push(`Repeat event: ${repetitionError}.`);

  if (!value.tickets || value.tickets.length === 0) {
    errors.push('At least one ticket is required.');
  } else {
    const paidTicketCount = value.tickets.filter((ticket) => Number(ticket.price || 0) > 0).length;
    value.tickets.forEach((ticket, index) => {
      if (!ticket.title) errors.push(`Ticket ${index + 1}: title is required.`);
      if (ticket.quantity === undefined || ticket.quantity === null || Number.isNaN(Number(ticket.quantity))) {
        errors.push(`Ticket ${index + 1}: quantity is required.`);
      }
    });
    if (isPaidEvent && paidTicketCount === 0) {
      errors.push('A paid event must have at least one ticket with a price greater than zero.');
    }
    if (!isPaidEvent && paidTicketCount > 0) {
      errors.push('A free event must not include tickets with a price greater than zero.');
    }
  }

  return errors;
};

const setFieldError = <K extends keyof EventErrors>(
  setErrors: React.Dispatch<React.SetStateAction<EventErrors>>,
  key: K,
  message?: string,
): string | undefined => {
  setErrors((prev) => {
    if (prev[key] === message) return prev;
    return { ...prev, [key]: message };
  });
  return message;
};

export const validateEventFormField = (params: {
  value: EventFormValues;
  setErrors: React.Dispatch<React.SetStateAction<EventErrors>>;
  key: keyof EventFormValues;
  isPaidEvent: boolean;
}): string | undefined => {
  const { value, setErrors, key, isPaidEvent } = params;

  if (key === 'title') {
    return setFieldError(setErrors, 'title', value.title ? undefined : 'required');
  }
  if (key === 'markdown_description') {
    const message = value.markdown_description ? undefined : 'required';
    setErrors((prev) => {
      if (prev.markdown_description === message) return prev;
      return { ...prev, markdown_description: message };
    });
    return message;
  }
  if (key === 'type') {
    return setFieldError(setErrors, 'type', value.type ? undefined : 'required');
  }
  if (key === 'event_profile_id') {
    return setFieldError(setErrors, 'event_profile_id', value.event_profile_id ? undefined : 'required');
  }
  if (key === 'location_type') {
    return setFieldError(setErrors, 'location_type', value.location_type ? undefined : 'required');
  }
  if (key === 'location') {
    const needsLocation = ['in-person', 'hybrid'].includes(value.location_type);
    return setFieldError(setErrors, 'location', !needsLocation || !!value.location ? undefined : 'required');
  }
  if (key === 'feedback_form_id') {
    const needsFeedbackForm = ['noAttendance', 'full'].includes(value.flow_option);
    return setFieldError(
      setErrors,
      'feedback_form_id',
      !needsFeedbackForm || !!value.feedback_form_id ? undefined : 'required',
    );
  }
  if (key === 'start_date' || key === 'end_date') {
    const isOnDemand = value.type === 'on-demand';
    const message = isOnDemand
      ? value.start_date || value.end_date
        ? 'on-demand events should not have start/end dates'
        : undefined
      : !value.start_date || !value.end_date
        ? 'required'
        : value.end_date < value.start_date
          ? 'end must be after start'
          : undefined;
    setErrors((prev) => {
      if (prev.start_date === message && prev.end_date === message) return prev;
      return { ...prev, start_date: message, end_date: message };
    });
    return message;
  }
  if (key === 'tickets') {
    if (!value.tickets || value.tickets.length === 0) {
      return setFieldError(setErrors, 'tickets', 'at least one ticket required');
    }
    const missingTitle = value.tickets.some((ticket) => !ticket.title);
    const missingQuantity = value.tickets.some((ticket) => ticket.quantity === undefined || ticket.quantity === null);
    const paidTicketCount = value.tickets.filter((ticket) => Number(ticket.price || 0) > 0).length;
    if (missingTitle) return setFieldError(setErrors, 'tickets', 'all tickets must have a title');
    if (missingQuantity) return setFieldError(setErrors, 'tickets', 'all tickets must have a quantity');
    if (isPaidEvent && paidTicketCount === 0) {
      return setFieldError(
        setErrors,
        'tickets',
        'a paid event must have at least one ticket with a price greater than zero',
      );
    }
    if (!isPaidEvent && paidTicketCount > 0) {
      return setFieldError(
        setErrors,
        'tickets',
        'a free event must not include tickets with a price greater than zero',
      );
    }
    return setFieldError(setErrors, 'tickets', undefined);
  }

  return undefined;
};

export const updateEventFormValue = (
  previousValue: EventFormValues,
  updater: (prev: EventFormValues) => EventFormValues,
): EventFormValues => {
  const nextValue = {
    ...updater(previousValue),
  };
  nextValue.plain_description = deriveEventPlainDescription(nextValue);
  return nextValue;
};
